import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
// components
import { IPLTimerTag } from "../components";
// context
import { CartIPLSwiggySixesContext } from "@core-ui/contexts/CartIPLSwiggySixesContext";
// types
import { CouponInfo, TimerUpdateSource } from "@swiggy-private/cart-types";
// utils
import { isValidNumber } from "@core-ui/helpers/Utils";
import { isIPLSwiggySixesOfferTag, generateTimerPayloadForSwiggySixAnalytics } from "@core-ui/helpers/Coupon";
import { isEmpty } from "@swiggy-private/js-utils";

export const useComputeTimerTag = ({ data }: { data: CouponInfo }) => {
    const { titleTimer, tagIdentifiers, applicable = false } = data;
    const [titleTimerTag, setTitleTimerTag] = useState<React.ReactNode>(null);
    const { tickerTimerInMinSec, validateAndSetTimestamp, timestampInMs } = useContext(CartIPLSwiggySixesContext);

    const setAndComputeIPLTimerTag = useCallback(() => {
        if (!isEmpty(titleTimer?.text)) {
            if (!isEmpty(titleTimer?.endTimeEpochInSec) && isValidNumber(titleTimer?.endTimeEpochInSec)) {
                // if timestamp is not set or if it is set and the endTimeEpochInSec is greater than the timestamp
                // then set the timestamp
                if (
                    isEmpty(timestampInMs) ||
                    (!isEmpty(timestampInMs) && Number(titleTimer?.endTimeEpochInSec) * 1000 > Number(timestampInMs))
                ) {
                    validateAndSetTimestamp?.(Number(titleTimer?.endTimeEpochInSec), TimerUpdateSource.FETCH_COUPONS);
                }
            }
            setTitleTimerTag(<IPLTimerTag titleTimer={titleTimer} tickerTimerInMinSec={tickerTimerInMinSec} />);
        }
    }, [tickerTimerInMinSec, timestampInMs, titleTimer, validateAndSetTimestamp]);

    const computeTitleTimer = useCallback(() => {
        if (applicable && isIPLSwiggySixesOfferTag(tagIdentifiers)) {
            setAndComputeIPLTimerTag();
        }
    }, [setAndComputeIPLTimerTag, tagIdentifiers, applicable]);

    const timerAnalyticsPayload = useMemo(() => {
        if (applicable && isIPLSwiggySixesOfferTag(tagIdentifiers)) {
            return generateTimerPayloadForSwiggySixAnalytics(tickerTimerInMinSec.enabled, timestampInMs);
        }
    }, [tagIdentifiers, tickerTimerInMinSec.enabled, timestampInMs, applicable]);

    useEffect(() => {
        computeTitleTimer();
    }, [computeTitleTimer]);

    return {
        timerAnalyticsPayload,
        titleTimerTag,
    };
};
